import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider } from "styled-components";

import { requestAlias } from "./store/requests/thunks/requestAlias";

import { setRequestsAsIdle } from "./store/requests/requestsSlice";

import SeasonView from "./views/SeasonView/SeasonView";
import ModelView from "./views/ModelView/ModelView";
import SizeView from "./views/SizeView/SizeView";
import DesignView from "./views/DesignView/DesignView";
import SummaryView from "./views/SummaryView/SummaryView";

import { Spinner } from "./components/Spinner/Spinner";
import { BreadCrumbList } from "./components/BreadCrumb/BreadCrumbList";

import { alert } from "./helpers/alert";

import { GlobalStyles } from "./GlobalStyles";
import { GlobalPrintStyles } from "./GlobalPrintStyles";
import { skbv, nordrum } from "./Themes";
import { skbv as muiSkbv, nordrum as muiNordrum} from "./muiThemes";

import { Main } from "./AppStyle";

const App = () => {
  const dispatch = useDispatch();
  const requests = useSelector((state) => state.requests);
  const init = useSelector((state) => state.init);

  const { channelId, baseRoute } = init;

  function getTheme(channelId) {
    switch (channelId) {
      case "1":
      case "2":
        return skbv;
      case "7":
      case "8":
        return nordrum;
      default:
        return skbv;
    }
  }
  
  function getMuiTheme(channelId) {
    switch (channelId) {
      case "1":
      case "2":
        return muiSkbv;
      case "7":
      case "8":
        return muiNordrum;
      default:
        return muiSkbv;
    }
  }

  // On mount
  useEffect(() => {
    dispatch(requestAlias());
  }, [dispatch]);

  // Handle rejected
  useEffect(() => {
    if (requests.status.requestAlias === "rejected") {
      const theme = getTheme(channelId);

      alert({
        title: "We are having technical issues.",
        text: "Please try again later.",
        icon: "error",
        confirmButtonColor: theme.primary,
        onConfirm: () => {
          dispatch(setRequestsAsIdle(["requestAlias"]));
        },
      });
    }
  });

  const renderMode = (() => {
    switch (requests.status.requestAlias) {
      case "idle":
      case "pending":
        return "loader";
      case "fulfilled":
        return "default";
      case "rejected":
        return "none";
      default:
        return null;
    }
  })();

  if (renderMode === "loader") {
    return (
      <ThemeProvider theme={getTheme(channelId)}>
        <Main>
          <Spinner />
        </Main>
      </ThemeProvider>
    );
  }

  if (renderMode === "default") {
    return (
      <ThemeProvider theme={getTheme(channelId)}>
        <MuiThemeProvider theme={getMuiTheme(channelId)}>
          <BrowserRouter>
            <BreadCrumbList />
            <Main>
              <GlobalStyles />
              <GlobalPrintStyles />
              <Routes>
                <Route path={`${baseRoute}/step1`} element={<SeasonView />} />
                <Route path={`${baseRoute}/step2`} element={<ModelView />} />
                <Route path={`${baseRoute}/step3`} element={<SizeView />} />
                <Route path={`${baseRoute}/step4`} element={<DesignView />} />
                <Route path={`${baseRoute}/step5`} element={<SummaryView />} />
                <Route path={`${baseRoute}/gotosummary`} element={<SummaryView />} />
                <Route path="*" element={<Navigate to={`${baseRoute}/step1`} replace />} />
              </Routes>
            </Main>
          </BrowserRouter>
        </MuiThemeProvider>
      </ThemeProvider>
    );
  }

  /*
  If status for request(s) is 'rejected' / renderMode is 'none'
  --> error handling (including UI) is done in useEffect
  */
  return null;
};

export default App;
